<script setup lang="ts">
// Add / edit drawer for admin operators. Layout mirrors the investor-web
// drawer convention: custom header (title + chip + manual close), body
// fills the panel with `min-height: 100%`, and the action button pins to
// the bottom via `margin-top: auto`. Errors do NOT escape via toast —
// field-level errors render through FormField, and a server error (e.g.
// duplicate email, locked record) renders as a Message just above the
// submit button so the operator stays in the form context.

import { ref, watch, computed } from 'vue'

import Drawer from '@uiKit/elements/Drawer.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'

import type { Admin, AdminRole, AdminStatus } from '../types/adminTypes'
import type { UpsertAdminPayload } from '../types/adminTypes'

// ── Public surface ──────────────────────────────────────────────────────

interface Props {
  /** Controls drawer visibility — v-model compatible. */
  visible: boolean
  /** `add` builds a blank form; `edit` hydrates from `admin`. */
  mode: 'add' | 'edit'
  /** Required for `edit` mode. */
  admin?: Admin | null
  /** Show pending state on the submit button. */
  loading?: boolean
  /** Server-side error message from the parent's mutation. */
  apiError?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  admin: null,
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'submit', payload: UpsertAdminPayload): void
}>()

// ── Reference data ──────────────────────────────────────────────────────

const ROLE_OPTIONS: { label: string; value: AdminRole }[] = [
  { label: 'Super Admin', value: 'Super Admin' },
  { label: 'User Manager', value: 'User Manager' },
  { label: 'Content Manager', value: 'Content Manager' },
  { label: 'Reports Viewer', value: 'Reports Viewer' },
  { label: 'Support Manager', value: 'Support Manager' },
]

const STATUS_OPTIONS: { label: string; value: AdminStatus }[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
]

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// ── Local form state ────────────────────────────────────────────────────

const name = ref('')
const email = ref('')
const role = ref<AdminRole>('User Manager')
const status = ref<AdminStatus>('Active')

const nameError = ref('')
const emailError = ref('')
const submitted = ref(false)

// Investor-web's "show server error until the user edits again" pattern:
// once any input changes after a server failure, hide the message so the
// user isn't stuck staring at a stale error. The watcher resets the flag
// when a new error arrives so the next submit re-displays it.
const hasEditedSinceApiError = ref(false)

watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)

const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

// Re-hydrate form whenever the drawer opens or the target admin changes.
// Closing the drawer clears the errors so the next open starts pristine.
watch(
  () => [props.visible, props.admin] as const,
  ([open, admin]) => {
    if (!open) {
      nameError.value = ''
      emailError.value = ''
      submitted.value = false
      hasEditedSinceApiError.value = false
      return
    }
    name.value = admin?.name ?? ''
    email.value = admin?.email ?? ''
    role.value = admin?.role ?? 'User Manager'
    status.value = admin?.status ?? 'Active'
  },
  { immediate: true },
)

// ── Validation ──────────────────────────────────────────────────────────

const validate = (): boolean => {
  nameError.value = name.value.trim() ? '' : 'Full name is required.'
  if (!email.value.trim()) emailError.value = 'Email address is required.'
  else if (!EMAIL_RE.test(email.value)) emailError.value = 'Enter a valid email.'
  else emailError.value = ''
  return !nameError.value && !emailError.value
}

function markEdited() {
  hasEditedSinceApiError.value = true
}

function updateName(value: string) {
  name.value = value
  if (submitted.value) nameError.value = value.trim() ? '' : 'Full name is required.'
  markEdited()
}

function updateEmail(value: string) {
  email.value = value
  if (submitted.value) {
    if (!value.trim()) emailError.value = 'Email address is required.'
    else if (!EMAIL_RE.test(value)) emailError.value = 'Enter a valid email.'
    else emailError.value = ''
  }
  markEdited()
}

// ── Handlers ────────────────────────────────────────────────────────────

const handleSubmit = () => {
  submitted.value = true
  if (!validate()) return
  emit('submit', {
    name: name.value.trim(),
    email: email.value.trim(),
    role: role.value,
    status: status.value,
  })
}

const handleClose = () => {
  if (props.loading) return
  emit('update:visible', false)
}

const headerLabel = computed(() => (props.mode === 'add' ? 'Invite admin' : 'Edit admin'))
const headerChip = computed(() => (props.mode === 'add' ? 'New' : 'Editing'))
const submitLabel = computed(() => (props.mode === 'add' ? 'Send invite' : 'Save changes'))
</script>

<template>
  <Drawer
    :model-value="visible"
    position="right"
    width="480px"
    :show-close-icon="false"
    :dismissable="!loading"
    @update:model-value="(v: boolean) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader
        :title="headerLabel"
        :chip-label="headerChip"
        :disable-close="loading"
        @close="handleClose"
      />
    </template>

    <Box flex direction="col" gap="4" class="form-drawer__body">
      <Box flex direction="col" gap="4" class="form-drawer__content">
        <Text type="body" color="muted">
          {{
            mode === 'add'
              ? 'Invite a new operator to the admin portal. They will receive a sign-in link by email.'
              : 'Update permissions and status. Changes apply immediately and are recorded in the audit log.'
          }}
        </Text>

        <Box flex direction="col" gap="3">
          <FormField label="Full name" :error="nameError" required>
            <Input
              :model-value="name"
              placeholder="e.g. Reema A."
              :invalid="!!nameError"
              @update:model-value="updateName"
            />
          </FormField>

          <FormField label="Email address" :error="emailError" required>
            <Input
              :model-value="email"
              type="email"
              placeholder="user@platform.com"
              :invalid="!!emailError"
              @update:model-value="updateEmail"
            />
          </FormField>

          <FormField label="Role" required>
            <Select
              v-model="role"
              :options="ROLE_OPTIONS"
              option-label="label"
              option-value="value"
              placeholder="Select role"
            />
          </FormField>

          <FormField label="Status" required>
            <Select
              v-model="status"
              :options="STATUS_OPTIONS"
              option-label="label"
              option-value="value"
              placeholder="Select status"
            />
          </FormField>
        </Box>

        <Message
          v-if="visibleApiError"
          color="danger"
          variant="outlined"
          :message="visibleApiError"
        />
      </Box>

      <Box class="form-drawer__actions">
        <Button :label="submitLabel" block size="large" :loading="loading" @click="handleSubmit" />
      </Box>
    </Box>
  </Drawer>
</template>

<style scoped>
/* Body fills the panel; the action block uses `margin-top: auto` so it
   pins to the bottom when content is shorter than the viewport and
   scrolls naturally when longer. Mirrors the investor-web pattern. */
.form-drawer__body {
  width: 100%;
  min-height: 100%;
  padding: 1.25rem 1.5rem;
  box-sizing: border-box;
}

.form-drawer__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .form-drawer__body {
    padding: 1rem;
  }
}
</style>
